import { useState, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Plus, UserRound } from 'lucide-react'
import { InlineFieldInput } from '@/components/inline-field-group'
import { LoadingButton } from '@/components/loading-button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { QuoteBuilderApiError, useQuoteBuilderApi } from '../api/client'
import { invalidateQuoteOverview } from '../api/queries'
import type { Address, Customer, NewCustomer, Quote, QuoteStartValues } from '../api/types'
import { AqprojImportAction } from '../imports/aqproj-import'
import { PageHeader } from '../ui/page'
import { blankAddress } from './address'
import { AddressPanel, Field } from './address-panel'
import { CustomerSearch } from './customer-search'

const blankCustomer = (): NewCustomer => ({
  company_name: '',
  contact_name: '',
  email: '',
  phone: '',
  billing_address: blankAddress(),
  shipping_address: blankAddress(),
})

function withCustomerContact(
  address: Address,
  customer: Pick<NewCustomer, 'company_name' | 'contact_name' | 'email' | 'phone'>,
): Address {
  return {
    ...address,
    name: customer.company_name,
    contact_name: customer.contact_name,
    email: customer.email,
    phone: customer.phone || '',
  }
}

function startError(error: Error | null) {
  if (!error) return ''
  if (error instanceof QuoteBuilderApiError && error.status === 422) return 'Check the project name and customer details and try again.'
  return 'The quote could not be created. Try again.'
}

/**
 * New quote (demo `routes/quotes.new.tsx`): name the project; the customer and addresses are
 * optional and can be added later (USA-1140).
 */
export function NewQuotePage() {
  const navigate = useNavigate()
  return (
    <div className="mx-auto w-full max-w-7xl">
      <PageHeader actions={<AqprojImportAction />} title="New quote" />
      <CustomerInfoStart
        onQuoteCreated={(quote) =>
          void navigate({ to: '/quotes/$quoteId/workflow/$stage', params: { quoteId: quote.id, stage: 'customer' } })
        }
      />
    </div>
  )
}

export function CustomerInfoStart({ onQuoteCreated }: { onQuoteCreated: (quote: Quote) => void }) {
  const api = useQuoteBuilderApi()
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<'existing' | 'new'>('existing')
  const [selected, setSelected] = useState<Customer | null>(null)
  const [newCustomer, setNewCustomer] = useState(blankCustomer)
  const [title, setTitle] = useState('')
  const [billTo, setBillTo] = useState(blankAddress)
  const [deliverTo, setDeliverTo] = useState(blankAddress)

  const create = useMutation({
    mutationFn: (values: QuoteStartValues) =>
      api.request<Quote>('/quotes/start', { method: 'POST', body: JSON.stringify(values) }),
    onSuccess: (quote) => {
      queryClient.setQueryData(['quote', quote.id], quote)
      void invalidateQuoteOverview(queryClient)
      void queryClient.invalidateQueries({ queryKey: ['customer-search'] })
      onQuoteCreated(quote)
    },
  })

  const chooseCustomer = (customer: Customer) => {
    setSelected(customer)
    setBillTo(customer.billing_address)
    setDeliverTo(customer.shipping_address)
  }
  const updateBillTo = (value: Address) => {
    setBillTo(value)
    if (mode === 'new') setNewCustomer((customer) => ({ ...customer, billing_address: value }))
  }
  const updateDeliverTo = (value: Address) => {
    setDeliverTo(value)
    if (mode === 'new') setNewCustomer((customer) => ({ ...customer, shipping_address: value }))
  }
  const startCustomer =
    mode === 'existing' && selected
      ? { company_name: selected.company_name, contact_name: selected.contact_name, email: selected.email, phone: selected.phone }
      : newCustomer
  // The customer is optional (USA-1140): a blank New customer form starts the quote without one.
  // Naming a company saves it as a customer, which then needs its contact and email.
  const savesNewCustomer = mode === 'new' && Boolean(newCustomer.company_name.trim())
  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!title.trim()) return
    create.mutate({
      ...(savesNewCustomer
        ? { new_customer: newCustomer }
        : mode === 'existing' && selected
          ? { customer_id: selected.id }
          : {}),
      title: title.trim(),
      bill_to: billTo,
      deliver_to: deliverTo,
    })
  }
  const setNew = (key: 'company_name' | 'contact_name' | 'email' | 'phone', value: string) =>
    setNewCustomer((customer) => ({ ...customer, [key]: value }))

  return (
    <form className="grid gap-5" onSubmit={submit}>
      <section className="rounded-xl border bg-card p-4 sm:p-5">
        <Field label="Project name">
          {(id) => (
            <InlineFieldInput
              autoFocus
              className="h-9 text-base"
              id={id}
              maxLength={255}
              onChange={(event) => setTitle(event.target.value)}
              required
              value={title}
            />
          )}
        </Field>
      </section>

      <Tabs
        onValueChange={(value) => {
          if (value === 'new') {
            setSelected(null)
            setBillTo(newCustomer.billing_address ?? blankAddress())
            setDeliverTo(newCustomer.shipping_address ?? blankAddress())
          }
          setMode(value as 'existing' | 'new')
        }}
        value={mode}
      >
        <TabsList aria-label="Customer source">
          <TabsTrigger className="px-3" value="existing">Existing customer</TabsTrigger>
          <TabsTrigger className="px-3" value="new">New customer</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1.1fr)_minmax(280px,0.9fr)]">
        <section className="grid content-start gap-4 rounded-xl border bg-card p-4 sm:p-5">
          <div className="flex items-center gap-2">
            <UserRound aria-hidden="true" className="size-4 text-muted-foreground" />
            <h2 className="text-base font-semibold">Customer</h2>
            <span className="text-xs text-muted-foreground">Optional</span>
          </div>
          {mode === 'existing' ? (
            <CustomerSearch disabled={create.isPending} onSelect={chooseCustomer} selectedId={selected?.id} />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Company">
                {(id) => <InlineFieldInput id={id} onChange={(event) => setNew('company_name', event.target.value)} value={newCustomer.company_name} />}
              </Field>
              <Field label="Contact">
                {(id) => <InlineFieldInput id={id} onChange={(event) => setNew('contact_name', event.target.value)} required={savesNewCustomer} value={newCustomer.contact_name} />}
              </Field>
              <Field label="Email">
                {(id) => <InlineFieldInput id={id} onChange={(event) => setNew('email', event.target.value)} required={savesNewCustomer} type="email" value={newCustomer.email} />}
              </Field>
              <Field label="Phone">
                {(id) => <InlineFieldInput id={id} onChange={(event) => setNew('phone', event.target.value)} type="tel" value={newCustomer.phone || ''} />}
              </Field>
            </div>
          )}
        </section>

        <section aria-label="Addresses" className="grid content-start gap-3">
          <AddressPanel
            actionLabel="Use customer"
            disabled={create.isPending}
            onAction={() => updateBillTo(withCustomerContact(billTo, startCustomer))}
            onChange={updateBillTo}
            title="Bill To"
            value={billTo}
          />
          <AddressPanel
            actionLabel="Use Bill To"
            disabled={create.isPending}
            onAction={() => updateDeliverTo({ ...billTo })}
            onChange={updateDeliverTo}
            title="Deliver To"
            value={deliverTo}
          />
        </section>
      </div>

      {create.error ? (
        <p className="text-sm text-destructive" role="alert">{startError(create.error)}</p>
      ) : null}
      <div className="flex justify-end">
        <LoadingButton
          className="w-full sm:w-auto"
          disabled={!title.trim()}
          loading={create.isPending}
          loadingText="Creating quote…"
          type="submit"
        >
          <Plus aria-hidden="true" />
          Create quote
        </LoadingButton>
      </div>
    </form>
  )
}
